var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { EventEmitter, utils as dataUtils, AggregationSettings, DataType } from '@easydata/core';
import { i18n } from '@easydata/core';
import { equtils } from '../utils/utils';
import { DataModel } from '../dm/data_model';
import { Expression } from './expression';
import { QueryColumn, SortDirection } from './query_column';
import { Condition } from './condition';
import { DataKind } from '../types/data_kind';
import { LinkType } from '../types/link_type';
import { ExprTag } from '../types/expr_tag';
import { CondTag } from '../types/cond_tag';
import { QueryChangePart } from './query_events';
/**
 * The error that appears when the query is not valid
 */
var InvalidQueryError = /** @class */ (function (_super) {
    __extends(InvalidQueryError, _super);
    /**
     * Creates an instance of error type.
     * @param message The error message
     */
    function InvalidQueryError(message) {
        var _this = _super.call(this, message) || this;
        if (Error.captureStackTrace) {
            Error.captureStackTrace(_this, InvalidQueryError);
        }
        _this.name = 'InvalidQueryError';
        return _this;
    }
    return InvalidQueryError;
}(Error));
export { InvalidQueryError };
/**
 * Represents Query.
 */
var Query = /** @class */ (function () {
    /**
     * The default constructor.
     * @param model The data model.
     * @param data JSON representation object.
     * @param options The options.
     * @throws {InvalidQueryError} The error throws when query is not valid.
     */
    function Query(model, data, options) {
        var _this = this;
        /**
         * Contains user-defined extra data for current query.
         */
        this.extraData = {};
        this.innerData = {};
        this.isNewbie = true;
        this._isModified = false;
        this._enableAggregation = false;
        this.parentQuery = null;
        this.drillDowns = [];
        this.eventEmitter = new EventEmitter(this);
        this.root = new Condition(this, CondTag.Group);
        this.extraConditions = new Condition(this, CondTag.Group);
        this.columns = new Array();
        this.justsorted = new Array();
        if (!this.id) {
            this.regenerateId();
        }
        this.setDefaultName();
        this.description = "";
        this.model = (model) ? model : new DataModel();
        if (data) {
            this.loadFromData(data);
        }
        this.attributeExprTag = (options) ? options.attrTag || ExprTag.EntityAttribute : ExprTag.EntityAttribute;
        this.clientListRequestHandler = (options) ? options.clientListRequestHandler : new Array();
        this.serverListRequestHandler = (options) ? options.serverListRequestHandler : new Array();
        this.timezoneOffset = (new Date()).getTimezoneOffset();
        this.aggrSettings = new AggregationSettings(this);
        if (options) {
            this.parentQuery = options.parent;
        }
        this.addChangedCallback(function (ev) {
            if (ev.data
                && ((ev.data.part & QueryChangePart.Columns) > 0)
                && !ev.data.aggregation) {
                if (_this._enableAggregation && _this.updateAggregationSettings()) {
                    _this.fireChangedEvent({
                        part: QueryChangePart.Aggregation
                    });
                }
            }
        });
    }
    Query.prototype.isModified = function () {
        return this._isModified;
    };
    Query.prototype.resetModified = function () {
        this._isModified = false;
    };
    Object.defineProperty(Query.prototype, "enableAggregation", {
        get: function () {
            return this._enableAggregation;
        },
        set: function (value) {
            var oldValue = this._enableAggregation;
            this._enableAggregation = value;
            if (!oldValue && value) {
                this.processQueryForAggregation();
                var aggrCols = this.columns.filter(function (c) { return c.enabled
                    && c.expr.tag == ExprTag.AggregateFunction; });
            }
            if (!this._enableAggregation) {
                this.aggrSettings.clear();
            }
        },
        enumerable: true,
        configurable: true
    });
    Query.prototype.updateAggregationSettings = function () {
        var hasChanged = false;
        var aggrSettings = this.getAggregationSettings();
        for (var i = aggrSettings.groups.length - 1; i >= 0; i--) {
            var group = aggrSettings.groups[i];
            var _loop_1 = function (j) {
                var colId = group.columns[j];
                if (!this_1.columns.find(function (col) { return col.id === colId; })) {
                    group.columns.splice(j, 1);
                    hasChanged = true;
                }
            };
            var this_1 = this;
            for (var j = group.columns.length - 1; j >= 0; j--) {
                _loop_1(j);
            }
            if (group.columns.length == 0) {
                aggrSettings.groups.slice(i, 1);
            }
        }
        var aggregates = aggrSettings.getAggregates();
        var _loop_2 = function (i) {
            var aggr = aggregates[i];
            if (!this_2.columns.find(function (col) { return col.id === aggr.colId; })) {
                aggregates.splice(i, 1);
                hasChanged = true;
            }
        };
        var this_2 = this;
        for (var i = aggregates.length - 1; i >= 0; i--) {
            _loop_2(i);
        }
        return hasChanged;
    };
    Query.prototype.createSubQuery = function () {
        return new Query(this.model);
    };
    Query.prototype.useAggregation = function (settingsTuner, silent) {
        if (silent === void 0) { silent = false; }
        this.aggrSettings.clear();
        settingsTuner(this.aggrSettings);
        if (this.enableAggregation) {
            this.processQueryForAggregation(silent);
        }
        else if (!silent) {
            console.warn('Aggregation is turned off. Turn it on with query.enableAggregation = true');
            return;
        }
    };
    Query.prototype.useDefaultAggregation = function () {
        var _this = this;
        this._enableAggregation = true;
        this.useAggregation(function (settings) {
            var aggrCols = _this.columns.filter(function (c) { return c.enabled
                && c.expr.tag == ExprTag.AggregateFunction; });
            if (aggrCols.length) {
                // query contains aggregate columns
                var colsToGroup = _this.columns.filter(function (c) { return c.enabled
                    && c.expr.tag == ExprTag.EntityAttribute; });
                var aggrsToAdd = [];
                for (var _i = 0, aggrCols_1 = aggrCols; _i < aggrCols_1.length; _i++) {
                    var col = aggrCols_1[_i];
                    aggrsToAdd.push({ id: col.id, func: col.expr.func });
                    _this.changeColumnType(col, ExprTag.EntityAttribute);
                }
                for (var _a = 0, aggrsToAdd_1 = aggrsToAdd; _a < aggrsToAdd_1.length; _a++) {
                    var aggr = aggrsToAdd_1[_a];
                    settings.addAggregateColumn(aggr.id, aggr.func);
                }
                for (var _b = 0, colsToGroup_1 = colsToGroup; _b < colsToGroup_1.length; _b++) {
                    var col = colsToGroup_1[_b];
                    settings.addGroup({ columns: [col.id], name: col.caption });
                }
                settings.addGrandTotals();
                _this.fireChangedEvent({
                    part: QueryChangePart.Columns,
                    wasModified: false
                });
            }
            else {
                // query does not has any aggr columns
                var colToAggr = _this.getColumns()
                    .filter(function (c) { return c.enabled && c.expr.tag == ExprTag.EntityAttribute
                    && _this.model.getAttributeById(c.expr.value).dataType === DataType.Currency; })[0];
                var colToGroup = _this.getColumns()
                    .filter(function (c) { return c.enabled && c.expr.tag == ExprTag.EntityAttribute
                    && _this.model.getAttributeById(c.expr.value).dataType === DataType.String; })[0];
                if ((!colToAggr && !settings.hasAggregates()) || !colToGroup)
                    return;
                settings.addGrandTotals()
                    .addAggregateColumn(colToAggr.id, 'SUM')
                    .addGroup({ columns: [colToGroup.id], name: 'Default' });
            }
        });
    };
    Query.prototype.processQueryForAggregation = function (silent) {
        if (silent === void 0) { silent = false; }
        var aggrSettings = this.getAggregationSettings();
        var groups = aggrSettings.getGroups();
        if (groups.length > 0) {
            var groupColIds = groups[groups.length - 1].columns;
            //take the list of grouped columns from the latest group
            var groupedCols = [];
            var sortedCols = [];
            for (var _i = 0, _a = this.getColumns(); _i < _a.length; _i++) {
                var col = _a[_i];
                if (groupColIds.indexOf(col.id) >= 0) {
                    groupedCols.push(col);
                }
                else if (col.sorting != SortDirection.None) {
                    sortedCols.push(col);
                }
            }
            var sortIndex_1 = 0;
            groupedCols.forEach(function (col) {
                if (col.sorting == SortDirection.None) {
                    col.sorting = SortDirection.Ascending;
                }
                col.sortIndex = sortIndex_1++;
            });
            sortedCols.forEach(function (col) {
                col.sortIndex = sortIndex_1++;
            });
            if (!silent) {
                this.fireChangedEvent({
                    part: QueryChangePart.Columns,
                    aggregation: true,
                    wasModified: false
                });
            }
        }
    };
    Query.prototype.getAggregationSettings = function () {
        return this.aggrSettings;
    };
    Query.prototype.getColumnIds = function (from, to) {
        to = to || from;
        return this.columns.slice(from, to - from + 1)
            .map(function (c) { return c.id; });
    };
    /**
     * Checks if the columns with IDs passed in function's parameter exist and are valid
     */
    Query.prototype.validateColumns = function (colIds) {
        var enabledColIds = this.columns.filter(function (c) { return c.enabled; }).map(function (c) { return c.id; });
        var prevIndex = -1;
        for (var _i = 0, colIds_1 = colIds; _i < colIds_1.length; _i++) {
            var col = colIds_1[_i];
            var index = enabledColIds.indexOf(col);
            if (index < 0)
                return false;
            if (prevIndex > index)
                return false;
            prevIndex = index;
        }
        return true;
    };
    Query.prototype.validateAggregate = function (colId, funcId) {
        var col = this.columns.filter(function (c) { return c.enabled && c.id === colId; })[0];
        var func = this.model.findAggrFunctionById(funcId);
        if (!func || !col || func.appliedTypes.indexOf(col.expr.dataType) < 0)
            return false;
        return true;
    };
    Query.prototype.isEx = function () {
        return false;
    };
    /**
     * Resets the query: clears all its conditions and columns, regenerate the ID and sets the default name.
     * @param silent Indicates whether we need to avoid firing the QueryChanged event.
     */
    Query.prototype.reset = function (clearQuery) {
        if (clearQuery === void 0) { clearQuery = true; }
        if (clearQuery) {
            this.clear();
        }
        this.regenerateId();
        this.setDefaultName();
        this._isModified = false;
        this.isNewbie = true;
    };
    Query.prototype.hasEnabledAggrColumns = function () {
        return this.columns.filter(function (col) { return col.enabled
            && col.expr.tag === ExprTag.AggregateFunction; }).length > 0;
    };
    Query.prototype.setDefaultName = function () {
        this.setName(i18n.getText('NewQueryName'));
        return this.getName();
    };
    /**
     * Validates wether the query corresponds to the model.
     * @param options Validate options.
     * @throws {InvalidQueryError} If query is not valid.
     */
    Query.prototype.validate = function (options) {
        options = options || {};
        var entityAttrNotExistError = "EntityAttribute isn't declared in the model: ";
        var wrongExprTag = "The expression has wrond tag: ";
        if (!this.model) {
            throw new InvalidQueryError("Model is not declared");
        }
        if (this.columns) {
            for (var _i = 0, _a = this.columns; _i < _a.length; _i++) {
                var column = _a[_i];
                if (column.expr.tag == ExprTag.EntityAttribute
                    || column.expr.tag === ExprTag.ParentEntityAttribute) {
                    var attr = this.model.getAttributeById(column.expr.value);
                    if (!attr) {
                        throw new InvalidQueryError(entityAttrNotExistError + column.expr.value);
                    }
                }
                else if (column.expr.tag === ExprTag.AggregateFunction) {
                    var attr = this.model.getAttributeById(column.expr.args[0].value);
                    if (!attr) {
                        throw new InvalidQueryError(entityAttrNotExistError + column.expr.args[0].value);
                    }
                }
                else if (column.expr.tag !== ExprTag.CustomSql) {
                    throw new InvalidQueryError(wrongExprTag + column.expr.tag);
                }
            }
        }
        if (this.root && this.root.getConditions()) {
            this.validateConditionGroup(this.root, options);
        }
    };
    Query.prototype.getAggregatedColumns = function () {
        return this.getColumns().filter(function (col) { return col.enabled
            && col.expr.tag === ExprTag.AggregateFunction; });
    };
    /**
     * Validates wether the query corresponds to the model.
     * @param options Validate options.
     * @returns `true` if it is valid, otherwise `false`
     */
    Query.prototype.tryValidate = function (options) {
        try {
            this.validate(options);
            return true;
        }
        catch (e) {
            if (e instanceof InvalidQueryError) {
                return false;
            }
            else {
                throw e;
            }
        }
    };
    Query.prototype.validateConditionGroup = function (curCond, options) {
        if (curCond.isGroup()) {
            for (var _i = 0, _a = curCond.getConditions(); _i < _a.length; _i++) {
                var condition = _a[_i];
                if (!condition.enabled)
                    continue;
                this.validateConditionGroup(condition, options);
            }
        }
        else if (curCond.expressions[0].tag === ExprTag.EntityAttribute) {
            var attrId = curCond.expressions[0].value;
            var attr = this.model.getAttributeById(attrId);
            if (!attr) {
                throw new InvalidQueryError("EntityAttribute isn't declared in the model: " + attrId);
            }
            if (!options.checkExpressions)
                return;
            for (var i = 1; i < curCond.expressions.length; i++) {
                var expr = curCond.expressions[i];
                if (expr.tag !== ExprTag.Constant)
                    continue;
                //validate number exprs
                if (dataUtils.isNumericType(expr.dataType)) {
                    if (!expr.value)
                        continue;
                    var values = [];
                    if (expr.kind === DataKind.List) {
                        values = expr.value.split(/\s*,\s*/);
                    }
                    else {
                        values = [expr.value];
                    }
                    for (var _b = 0, values_1 = values; _b < values_1.length; _b++) {
                        var value = values_1[_b];
                        var parsedVal = +value;
                        if (isNaN(parsedVal)) {
                            throw new InvalidQueryError("Expression constant is NaN in condition:" + curCond.id);
                        }
                        if (dataUtils.isIntType(expr.dataType) && parsedVal != parseInt(value, 10)) {
                            throw new InvalidQueryError("Expression constant is wrong interger in condition: " + curCond.id);
                        }
                    }
                }
            }
        }
    };
    /**
     * Sets data to the query.
     * @param data JSON representation object or JSON string
     * @param changeStatus Indicate whether we should change the "isModified" status (by default == `true`)
     */
    Query.prototype.loadFromDataOrJson = function (data, options) {
        if (data) {
            if (typeof data === "string") {
                this.loadFromJson(data, options);
            }
            else {
                this.loadFromData(data, options);
            }
        }
    };
    /**
     * Loads query from JSON.
     * @param json The JSON string.
     * @param options The options.
     * @throws {InvalidQueryError} The error throws when query is not valid.
     */
    Query.prototype.loadFromJson = function (json, options) {
        var data = JSON.parse(json);
        this.loadFromData(data, options);
    };
    /**
     * Loads query from data representation.
     * @param data The query DTO.
     * @param options The options.
     * @throws {InvalidQueryError} The error throws when query is not valid.
     */
    Query.prototype.loadFromData = function (data, options) {
        var _this = this;
        options = dataUtils.assign({
            changeStatus: true,
            validate: true
        }, options || {});
        var noErrors = false;
        this.clear();
        this.clearDrillDowns();
        if (data) {
            this.id = data.id;
            this.root.loadFromData(this.model, data.root);
            if (data.root && data.root.conds) {
                for (var i = 0; i < data.root.conds.length; i++) {
                    var condData = data.root.conds[i];
                    if (condData.conds && !this.isEx()) {
                        console.error("Community version doesn't support working with groups.");
                        continue;
                    }
                    var cond = this.createCondition();
                    cond.loadFromData(this.model, condData);
                    this.root.addCondition(cond);
                }
            }
            this.name = data.name;
            this.description = data.desc;
            this.justsorted = [];
            if (data.justsortcols) {
                for (var i = 0; i < data.justsortcols.length; i++) {
                    var newCol = this.createColumn(true);
                    newCol.loadFromData(this.model, data.justsortcols[i]);
                    this.justsorted.push(newCol);
                }
            }
            if (data.cols) {
                for (var i = 0; i < data.cols.length; i++) {
                    var newCol = this.createColumn();
                    newCol.loadFromData(this.model, data.cols[i]);
                    this.columns.push(newCol);
                }
            }
            if (data.extraData) {
                this.extraData = data.extraData;
            }
            this.innerData = data.innerData || this.innerData;
            if (this.innerData && this.innerData.aggr) {
                this._enableAggregation = this.innerData.aggr.enabled || false;
                this.useAggregation(function (settings) {
                    settings.loadFromData(_this.innerData.aggr);
                }, true);
            }
            if (data.dds && data.dds.length) {
                for (var _i = 0, _a = data.dds; _i < _a.length; _i++) {
                    var dd = _a[_i];
                    var query = this.addDrillDown();
                    query.loadFromData(dd);
                }
            }
        }
        noErrors = true;
        if (noErrors) {
            if (options.changeStatus) {
                this.isNewbie = false;
                this._isModified = false;
            }
        }
        if (options.validate) {
            this.validate();
        }
    };
    /**
     *
     */
    Query.prototype.addDrillDown = function () {
        var query = this.model.createQuery();
        query.parentQuery = this;
        this.drillDowns.push(query);
        return query;
    };
    Query.prototype.getDrillDowns = function () {
        return this.drillDowns;
    };
    Query.prototype.getParentQuery = function () {
        return this.parentQuery;
    };
    /**
     *
     */
    Query.prototype.findColumnById = function (colId) {
        for (var _i = 0, _a = this.columns; _i < _a.length; _i++) {
            var col = _a[_i];
            if (col.id === colId)
                return col;
        }
        return null;
    };
    /**
     * Gets DataModel object associated with this query.
     * @retruns The data model.
     */
    Query.prototype.getModel = function () {
        return this.model;
    };
    /**
     * Sets DataModel object associated with this query.
     * @param model The data model.
     */
    Query.prototype.setModel = function (model) {
        this.model = model;
    };
    /**
     * Loads data model.
     * @param model JSON representation object or JSON string.
     */
    Query.prototype.loadModelData = function (model) {
        this.model.setData(model);
    };
    /**
     * Returns JSON representation of this query.
     * @retruns JSON string.
     */
    Query.prototype.toJSON = function () {
        return JSON.stringify(this.toJSONData());
    };
    /**
     * Returns JSON representation of this query.
     * @retruns JSON representation object.
     */
    Query.prototype.toJSONData = function () {
        var _this = this;
        var obj = {
            modelId: this.model ? this.model.getId() : null,
            modelName: this.model ? this.model.getName() : null
        };
        obj.id = this.id;
        obj.name = this.name;
        if (this.description) {
            obj.desc = this.description;
        }
        obj.cols = [];
        for (var i = 0; i < this.columns.length; i++) {
            obj.cols.push(this.columns[i].saveToData());
        }
        obj.justsortcols = [];
        for (var i = 0; i < this.justsorted.length; i++) {
            obj.justsortcols.push(this.justsorted[i].saveToData());
        }
        if (this.extraData) {
            obj.extraData = this.extraData;
        }
        if (this.innerData) {
            obj.innerData = this.innerData;
            obj.innerData.aggr = this.aggrSettings.saveToData();
            obj.innerData.aggr.enabled = this._enableAggregation;
        }
        var rootCondToData = function (condGroup) {
            var result = condGroup.saveToData();
            result.conds = [];
            var conditions = condGroup.getConditions();
            for (var i = 0; i < conditions.length; i++) {
                if (conditions[i].isGroup() && !_this.isEx()) {
                    console.error("Community version doesn't support working with groups.");
                    continue;
                }
                result.conds.push(conditions[i].saveToData());
            }
            return result;
        };
        obj.root = rootCondToData(this.root);
        obj.extraConds = rootCondToData(this.extraConditions);
        if (this.drillDowns.length > 0) {
            obj.dds = [];
            for (var _i = 0, _a = this.drillDowns; _i < _a.length; _i++) {
                var dd = _a[_i];
                obj.dds.push(dd.toJSONData());
            }
        }
        obj.timezoneOffset = this.timezoneOffset;
        obj.locale = i18n.getCurrentLocale();
        return obj;
    };
    /**
     * Checks wether query does not contains any condtion.
     * @retruns `true` if query does not contain any condition, otherwise `false`.
     */
    Query.prototype.isEmptyConditions = function () {
        return this.root.isEmpty();
    };
    /**
     * Checks wether query does not contains any column.
     * @retruns `true` if query does not contain any column, otherwise `false`.
     */
    Query.prototype.isEmptyColumns = function () {
        return !this.columns || this.columns.length === 0;
    };
    /**
     * Checks wether query does not contains any column and any condition.
     * @retruns `true` if query does not contain any column and any condition, otherwise `false`.
     */
    Query.prototype.isEmpty = function () {
        return this.isEmptyColumns() && this.isEmptyConditions();
    };
    /**
     * Clears the query (all conditions, columns, extraData, etc).
     */
    Query.prototype.clear = function () {
        this._enableAggregation = false;
        this.aggrSettings.clear();
        this.clearColumns();
        this.clearConditions();
        this.clearExtraConditions();
        this.innerData = {};
        this.extraData = {};
    };
    /**
     * Clears all columns in query
     */
    Query.prototype.clearColumns = function () {
        this.columns = [];
        this.justsorted = [];
    };
    /**
     * Clears all conditions in query.
     */
    Query.prototype.clearConditions = function () {
        this.root.clearConditions();
        this.root.linkType = LinkType.All;
        this.root.enabled = true;
    };
    /**
     * Clears all extra conditions in query
     */
    Query.prototype.clearExtraConditions = function () {
        this.extraConditions.clearConditions();
    };
    /**
     * Clears all drill downs
     */
    Query.prototype.clearDrillDowns = function () {
        this.drillDowns = [];
    };
    /**
     * Gets query ID.
     * @returns The ID>
     */
    Query.prototype.getId = function () {
        return this.id;
    };
    /**
     * Sets query ID.
     * @param id New Query ID.
     */
    Query.prototype.setId = function (id) {
        this.id = id;
    };
    /**
     * Regenerates current query ID.
     * @param id New Query ID.
     */
    Query.prototype.regenerateId = function () {
        this.id = dataUtils.generateId("quer");
    };
    /**
     * Gets name of the query.
     * @retruns The name.
     */
    Query.prototype.getName = function () {
        return this.name;
    };
    /**
     * Sets name of the query
     * @param name New name.
     */
    Query.prototype.setName = function (name) {
        this.name = name;
    };
    /**
     * Gets description of the query.
     * @retruns The description.
     */
    Query.prototype.getDescription = function () {
        return this.description;
    };
    /**
     * Sets description of the query
     * @param description New description.
     */
    Query.prototype.setDescription = function (description) {
        this.description = description;
    };
    /**
     * Get array of columns.
     * @retruns Columns.
     */
    Query.prototype.getColumns = function () {
        return this.columns;
    };
    /**
     * Gets array of columns that are used as keys
     * for totals.
     */
    Query.prototype.getUsedInTotalsColumns = function () {
        var aggrs = this.getAggregatedColumns();
        if (aggrs.length === 0)
            return [];
        var cols = this.getColumns()
            .filter(function (col) { return col.enabled && col.expr.tag === ExprTag.EntityAttribute; });
        if (cols.length)
            cols.pop();
        return cols;
    };
    /**
     * Get array of columns used only in sorting.
     * @retruns Columns.
     */
    Query.prototype.getJustSortedColumns = function () {
        return this.justsorted;
    };
    /**
     * Gets array of columns used in sorting
     * @retruns Columns
     */
    Query.prototype.getSortedColumns = function () {
        var sortedCols = new Array();
        var cols = this.getColumns()
            .concat(this.getJustSortedColumns());
        for (var i = 0; i < cols.length; i++) {
            var col = cols[i];
            if (col.sortIndex >= 0) {
                sortedCols.push(col);
            }
        }
        sortedCols.sort(function (col1, col2) {
            return col1.sortIndex - col2.sortIndex;
        });
        return sortedCols;
    };
    /**
     * Gets Colummn sorting index.
     * @param col The column.
     * @retruns The index.
     */
    Query.prototype.getColumnSortIndex = function (col) {
        if (col.sorting != SortDirection.None) {
            if (col.sortIndex < 0) {
                var cols = this.getColumns()
                    .concat(this.getJustSortedColumns());
                return dataUtils.indexOfArrayItem(cols, col);
            }
            else {
                return col.sortIndex;
            }
        }
        return -1;
    };
    Query.prototype.createColumn = function (justsorted) {
        if (justsorted === void 0) { justsorted = false; }
        return new QueryColumn(this, justsorted);
    };
    Query.prototype.getColumnById = function (id) {
        return dataUtils.findItemById(this.columns, id);
    };
    /**
     * Adds a new column with specified parameters
     * @param desriptor The descriptor of the new column. Can contain different parameters like:
     * `attribute` - a reference to a EntityAttr object,
     * `attributeId` - an ID of the entity attribute we need (if we don't indicate `attribute`),
     * `index`, sorting`, 'sortIndex` and other
     *
     * @param author The author of this operation. This parameter is used in EasyQuery widgets only to indicate which of the initiates the change.
     * @retruns The column.
     * @example
     * query.addColumn({
     *     attributeId: 'Customers.CompanyName',
     *     sortIndex: SortDirection.Ascending
     * }});
     */
    Query.prototype.addColumn = function (descriptor, author) {
        var columns = (descriptor.justsorted)
            ? this.justsorted
            : this.columns;
        var newColumn;
        if (descriptor.column) {
            newColumn = descriptor.column;
        }
        else {
            newColumn = this.createColumn(descriptor.justsorted);
            var attr = void 0;
            if (descriptor.attribute) {
                attr = descriptor.attribute;
            }
            else if (descriptor.attributeId) {
                attr = this.model.getAttributeById(descriptor.attributeId);
                if (!attr) {
                    throw new Error("Incorrect attribute id (" + descriptor.attributeId + "): addColumn");
                }
            }
            else {
                throw new Error('Not enought data for addColumn operation. Need attribute or attributeId');
            }
            if (!attr.useInResult) {
                if (attr.lookupAttr) {
                    attr = this.model.getAttributeById(attr.lookupAttr);
                }
                if (!attr || !attr.useInResult) {
                    throw new Error("Can't use this attribute for a column");
                }
            }
            if (descriptor.aggrFuncId && descriptor.cusomtSql) {
                console.warn("Column cannot be created using aggrFuncId and customSql at the same time. " +
                    "cusomSql will be used.");
            }
            if (descriptor.cusomtSql) {
                newColumn.caption = 'Custom SQL Column';
                newColumn.expr.tag = ExprTag.CustomSql;
                newColumn.expr.baseAttrId = attr.id;
                newColumn.expr.sql = descriptor.cusomtSql;
                newColumn.expr.kind = DataKind.Query;
                newColumn.expr.dataType = DataType.Unknown;
            }
            else if (descriptor.aggrFuncId) {
                var funcId = descriptor.aggrFuncId;
                newColumn.caption = attr.caption + ' '
                    + this.model.getAggrFunctionCaption(descriptor.aggrFuncId);
                newColumn.expr.tag = ExprTag.AggregateFunction;
                newColumn.expr.func = descriptor.aggrFuncId;
                newColumn.expr.kind = DataKind.Scalar;
                newColumn.expr.dataType = funcId == 'COUNT' || funcId == 'CNTDST' ? 4 : attr.dataType;
                newColumn.expr.distinct = false;
                var arg = new Expression(newColumn);
                arg.setValue(attr.id, true);
                arg.tag = ExprTag.EntityAttribute;
                arg.dataType = attr.dataType;
                newColumn.expr.args.push(arg);
            }
            else {
                newColumn.caption = attr.caption;
                newColumn.expr.setValue(attr.id, true);
                newColumn.expr.tag = ExprTag.EntityAttribute;
                newColumn.expr.dataType = attr.dataType;
            }
            newColumn.caption = descriptor.caption || newColumn.caption;
            newColumn.sorting = descriptor.sorting || SortDirection.None;
            newColumn.sortIndex = descriptor.sortIndex || -1;
            newColumn.enabled = dataUtils.getIfDefined(descriptor.enabled, true);
            newColumn.setReadOnly(dataUtils.getIfDefined(descriptor.readonly, false));
        }
        if (descriptor.index && descriptor.index >= 0 && descriptor.index < columns.length) {
            columns.splice(descriptor.index, 0, newColumn);
        }
        else {
            columns.push(newColumn);
        }
        return newColumn;
    };
    /**
     * Adds a new column with specified index.
     * @param column The column or array of columns
     * @param index The index of new column
     * @param author The author.
     * @example
     * const column = new Column();
     * column.caption = "Company name";
     *
     * query.addColumn(column, 1)
     */
    Query.prototype.addColumnObj = function (column, index, author) {
        var columns = this.columns;
        if (typeof column.length == "undefined") {
            if (typeof index == "number") {
                columns.splice(index, 0, column);
            }
            else {
                columns.push(column);
            }
        }
        else {
            if (typeof index == "number") {
                columns.push.apply(columns, [index, 0].concat(column));
            }
            else {
                columns.push.apply(columns, [].concat(column));
            }
        }
    };
    /**
     * Changes column to Aggregate function, custom expression or simple column.
     * @param column The column to change.
     * @param tag New column expression tag.
     * @param args The arguments (Contains aggregate function id)
     */
    Query.prototype.changeColumnType = function (column, tag, args) {
        if (column.isJustSorted())
            return;
        args = args || {};
        column.caption = (typeof args.caption !== 'undefined')
            ? args.caption
            : '';
        if (tag === ExprTag.AggregateFunction) {
            if (!args.funcId)
                throw Error('"funcId" argument is not defined');
            if (column.expr.tag == ExprTag.AggregateFunction) {
                column.expr.func = args.funcId;
                return;
            }
            var expr = new Expression(column);
            expr.tag = ExprTag.AggregateFunction;
            expr.func = args.funcId;
            expr.distinct = false;
            expr.dataType = args.funcId == 'COUNT' || args.funcId == 'CNTDST' ? 4 : column.expr.dataType;
            var arg = new Expression(column);
            arg.loadFromData(this.model, column.expr.saveToData());
            expr.args.push(arg);
            column.expr = expr;
        }
        else if (tag === ExprTag.CustomSql) {
            var attrId = (column.expr.tag == ExprTag.AggregateFunction)
                ? column.expr.args[0].value
                : column.expr.value;
            var sqlText = '';
            if (this.model) {
                sqlText = this.model.getAttributeById(attrId).expr;
            }
            var customSqlExpr = new Expression(column);
            customSqlExpr.tag = ExprTag.CustomSql;
            customSqlExpr.dataType = DataType.String; //the default data type
            customSqlExpr.sql = sqlText;
            customSqlExpr.baseAttrId = attrId;
            column.expr = customSqlExpr;
        }
        else if (tag === ExprTag.EntityAttribute) {
            if (column.expr.tag === ExprTag.EntityAttribute
                || column.expr.tag === ExprTag.ParentEntityAttribute) {
                return;
            }
            var exprId = (column.expr.tag === ExprTag.CustomSql)
                ? column.expr.baseAttrId
                : column.expr.args[0].value;
            var attr = this.model.getAttributeById(exprId);
            var expr = new Expression(column);
            expr.tag = ExprTag.EntityAttribute;
            expr.dataType = attr != null ? attr.dataType : DataType.Unknown;
            expr.setValue(exprId, true);
            column.expr = expr;
        }
    };
    /**
     * Moves the column with specified index to another position.
     * @param index1 The index of the column that should be moved.
     * @param index2 The index of the position the column should be moved to
     * @param author The author
     */
    Query.prototype.moveColumn = function (index1, index2) {
        var columns = this.getColumns();
        dataUtils.moveArrayItem(columns, index1, index2);
    };
    /**
     * Removes the column.
     * @param column The column to be removed.
     * @param author The author
     */
    Query.prototype.removeColumn = function (column, author) {
        var columns = (column.isJustSorted())
            ? this.getJustSortedColumns()
            : this.getColumns();
        dataUtils.removeArrayItem(columns, column);
    };
    /**
     * Adds a condition group - a group of simple conditions (predicates) linked by AND or OR
     * @param descriptor The descriptor of the new condition group.
     * @returns The new search condition.
     */
    Query.prototype.addConditionGroup = function (descriptor, addChildCondition) {
        if (addChildCondition === void 0) { addChildCondition = true; }
        console.error("Community version doesn't support working with groups.");
        return null;
    };
    Query.prototype.addExtraConditionGroup = function (descriptor, addChildCondition) {
        if (addChildCondition === void 0) { addChildCondition = true; }
        console.error("Community version doesn't support working with groups.");
        return null;
    };
    Query.prototype.createCondition = function (tag) {
        if (tag === void 0) { tag = CondTag.Simple; }
        return new Condition(this, tag);
    };
    /**
     *  Adds a new simple condition (a predicate)
     * @param descriptor The descriptor which defines different parameters of the new condition
     * @returns The new condition.
     */
    Query.prototype.addSimpleCondition = function (descriptor) {
        var parent = descriptor.parent || this.getRootCondition();
        var model = this.getModel();
        if (!model)
            return null;
        if (!descriptor.attribute && !descriptor.attributeId) {
            throw new Error("Either `attribute` or `attributeId` parameter must have a value");
        }
        if (!descriptor.attribute) {
            descriptor.attribute = model.getAttributeById(descriptor.attributeId);
            if (!descriptor.attribute) {
                throw new Error("Could not find this attribute: " + descriptor.attributeId);
            }
        }
        if (!descriptor.operator && descriptor.operatorId) {
            descriptor.operator = model.getOperatorById(descriptor.operatorId);
        }
        if (!descriptor.operator) {
            descriptor.operator = model.getDefaultOperatorForAttr(descriptor.attribute);
        }
        var cond = this.createSimpleConditionObject(descriptor.attribute, descriptor.operator, descriptor.value);
        parent.addCondition(cond);
        cond.enabled = dataUtils.getIfDefined(descriptor.enabled, true);
        cond.setReadOnly(dataUtils.getIfDefined(descriptor.readonly, false));
        return cond;
    };
    Query.prototype.addSimpleExtraCondition = function (descriptor) {
        descriptor.parent = descriptor.parent || this.extraConditions;
        return this.addSimpleCondition(descriptor);
    };
    Query.prototype.createSimpleConditionObject = function (attr, operator, inputValues) {
        if (!attr || !attr.useInConditions) {
            var attrCaption = attr ? attr.caption : 'NULL';
            throw "Can't add a condition with such attribute: " + attrCaption;
        }
        var cond = this.createCondition(CondTag.Simple);
        cond.setOperatorId(operator.id, true);
        var attrExpr = new Expression(cond);
        attrExpr.setValue(attr.id, true);
        attrExpr.tag = this.attributeExprTag;
        attrExpr.kind = DataKind.Attribute;
        attrExpr.dataType = attr.dataType;
        cond.expressions.push(attrExpr);
        var model = this.getModel();
        var valCount = operator.paramCount;
        var values = dataUtils.fillArray(new Array(valCount - 1), null);
        if (inputValues !== null && typeof inputValues !== "undefined") {
            if (Array.isArray(inputValues)) {
                dataUtils.copyArrayTo(inputValues, values);
            }
            else if (valCount > 0) {
                values[0] = inputValues;
            }
        }
        var valLength = values.length;
        for (var i = 0; i < valLength; i++) {
            var operand = model.getOperand(attr, operator, i + 1);
            cond.addExpressionByOperand(operand, values[i]);
        }
        return cond;
    };
    /**
     * Removes several columns passed as array.
     * @param columnsToRemove The list of columns to be removed.
     * @param author The author.
     */
    Query.prototype.removeColumns = function (columnsToRemove, author) {
        var justSortedColumns = columnsToRemove.filter(function (c) { return c.isJustSorted(); });
        var columns = columnsToRemove.filter(function (c) { return !c.isJustSorted(); });
        function removeFromArray(arr, arrToRemove) {
            var ctrLen = arrToRemove.length;
            for (var i = 0; i < ctrLen; i++) {
                var index = arr.indexOf(arrToRemove[i]);
                if (index >= 0) {
                    arr.splice(index, 1);
                }
            }
        }
        if (columns.length > 0) {
            removeFromArray(this.getColumns(), columns);
        }
        if (justSortedColumns.length > 0) {
            removeFromArray(this.getJustSortedColumns(), justSortedColumns);
        }
    };
    /**
     * Fires process event.
     * @param data The data.
     */
    Query.prototype.fireProcessEvent = function (data) {
        this.eventEmitter.fire('query.process', data);
    };
    Query.prototype.fireChangedEvent = function (params, postpone, wasModified) {
        params = params || { part: QueryChangePart.All };
        if (typeof (postpone) != 'undefined' || typeof (wasModified) != 'undefined') {
            params.postpone = postpone || 0;
            params.wasModified = wasModified;
            console.warn('Method with \'postpone\' and \'wasModified\' args is deprecated. Pass params in data object');
        }
        this._isModified = typeof (params.wasModified) != 'undefined'
            ? params.wasModified
            : true;
        var eventData = params || {
            part: QueryChangePart.All
        };
        this.eventEmitter.fire('query.change', eventData, params.postpone || 0);
    };
    Query.prototype.fireColumnsChangedEvent = function (action, column, justsorted) {
        if (justsorted === void 0) { justsorted = false; }
        this.fireChangedEvent({
            part: (justsorted)
                ? QueryChangePart.SortingColumns
                : QueryChangePart.Columns,
            action: action,
            changee: column
        });
    };
    Query.prototype.fireConditionsChangedEvent = function (action, condition) {
        this.fireChangedEvent({
            part: QueryChangePart.Conditions,
            action: action,
            changee: condition
        });
    };
    /**
     * Runs through condition with this callback.
     * @param processCondition The function to process condition.
     */
    Query.prototype.runThroughConditions = function (processCondition) {
        var processGroup = function (predicate) {
            for (var i = 0; i < predicate.getConditions().length; i++) {
                var cond = predicate.getConditions()[i];
                if (cond.isGroup()) {
                    processGroup(cond);
                }
                else {
                    if (processCondition)
                        processCondition(cond);
                }
            }
        };
        processGroup(this.getRootCondition());
    };
    /**
     * Gets one value for the attribute
     * @param attrId The attribute ID.
     * @retruns The value.
     */
    Query.prototype.getOneValueForAttr = function (attrId) {
        var res = null;
        this.runThroughConditions(function (cond) {
            if (cond.enabled) {
                var expr = cond.expressions[0];
                var opid = cond.getOperatorId();
                if (expr.value == attrId && cond.expressions[1] &&
                    (opid == "Equal" || opid == "InList" || opid == "StartsWith")) {
                    res = cond.expressions[1].value;
                }
            }
        });
        return res;
    };
    /**
     * Gets root predicate
     * @retruns The condition.
     */
    Query.prototype.getRootCondition = function () {
        return this.root;
    };
    /**
     * Add process callback
     * @param callback The callback.
     * @retruns The callback ID.
     */
    Query.prototype.addProcessCallback = function (callback) {
        return this.eventEmitter.subscribe('query.process', callback);
    };
    /**
     * Remove process callback
     * @param callbackId The callback ID.
     */
    Query.prototype.removeProcessCallback = function (callbackId) {
        this.eventEmitter.unsubscribe('query.process', callbackId);
    };
    /**
     * Add changed callback
     * @param callback The callback ID.
     */
    Query.prototype.addChangedCallback = function (callback) {
        return this.eventEmitter.subscribe('query.change', callback);
    };
    /**
     * Remove changed callback
     * @param callbackId The callback ID.
     */
    Query.prototype.removeChangedCallback = function (callbackId) {
        this.eventEmitter.unsubscribe('query.change', callbackId);
    };
    /**
     * Retruns conditions text
     */
    Query.prototype.getConditionsText = function () {
        var _this = this;
        var model = this.getModel();
        if (!model)
            return '';
        var getConditionGroupText = function (group) {
            var result = '', conjKey = "Conj" + equtils.linkTypeToStr(group.linkType), conjText = i18n.getText(conjKey), condText, i;
            for (i = 0; i < group.getConditions().length; i++) {
                condText = "";
                var cond = group.getConditions()[i];
                if (cond.enabled || typeof (cond.enabled) === "undefined") {
                    if (cond.isGroup()) {
                        condText = getConditionGroupText(cond);
                        if (condText) {
                            condText = "(" + condText + ")";
                        }
                    }
                    else {
                        var opId = cond.getOperatorId();
                        var operator = model.getOperatorById(opId);
                        var parts = equtils.parseOperatorFormat(operator);
                        var plen = parts.length;
                        for (var pi = 0; pi < plen; pi++) {
                            var part = parts[pi];
                            var txt = part.text;
                            if (part.type == "expression") {
                                var expr = cond.expressions[part.index];
                                if (expr.kind == DataKind.Attribute || expr.tag == ExprTag.EntityAttribute) {
                                    var attr = model.getAttributeById(expr.value);
                                    txt = model.getAttributeText(attr, "{entity} {attr}");
                                }
                                else {
                                    txt = expr.getText();
                                    if (_this.model.isDateMacro(txt) || _this.model.isTimeMacro(txt)) {
                                        var macroText = txt.substring(3, txt.length - 2);
                                        var res = i18n.getText(macroText);
                                        txt = res || txt;
                                    }
                                }
                            }
                            if (pi > 0)
                                condText += " ";
                            condText += txt;
                        }
                    }
                }
                if (condText) {
                    if (i > 0 && result) {
                        result += " " + conjText + " ";
                    }
                    result += condText;
                }
            }
            if (group.linkType == LinkType.None || group.linkType == LinkType.NotAll) {
                result = "not ( " + result + " )";
            }
            return result;
        };
        return getConditionGroupText(this.getRootCondition());
    };
    /**
    * Retruns conditions text
    */
    Query.prototype.getConditionsTextAsHtml = function () {
        var _this = this;
        var model = this.getModel();
        if (!model)
            return '<div class="eqjs-query-text"></div>';
        var quoteExprText = function (expr) {
            if (expr.kind == DataKind.Query) {
                return "&lt;" + expr.getText() + "&gt;";
            }
            else if (expr.kind == DataKind.List) {
                return "[" + expr.getText() + "]";
            }
            else if (expr.dataType == DataType.String
                || expr.dataType == DataType.Memo
                || expr.dataType == DataType.FixedChar) {
                return "&quot;" + expr.getText() + "&quot;";
            }
            else {
                var txt = expr.getText();
                if (_this.model.isDateMacro(txt) || _this.model.isTimeMacro(txt)) {
                    var macroText = txt.substring(3, txt.length - 2);
                    var res = i18n.getText(macroText);
                    txt = res || txt;
                }
                return txt;
            }
        };
        var getConditionGroupText = function (group) {
            var result = '', conjKey = "Conj" + equtils.linkTypeToStr(group.linkType), conjText = "<span class=\"eqjs-query-text-conj\">" + i18n.getText(conjKey) + "</span>", condText, i;
            for (i = 0; i < group.getConditions().length; i++) {
                condText = "";
                var cond = group.getConditions()[i];
                if (cond.enabled || typeof (cond.enabled) === "undefined") {
                    if (cond.isGroup()) {
                        condText = getConditionGroupText(cond);
                        if (condText) {
                            condText = "(" + condText + ")";
                        }
                    }
                    else {
                        var opId = cond.getOperatorId();
                        var operator = model.getOperatorById(opId);
                        var parts = equtils.parseOperatorFormat(operator);
                        var plen = parts.length;
                        for (var pi = 0; pi < plen; pi++) {
                            var part = parts[pi];
                            var txt = part.text;
                            if (part.type == "expression") {
                                var expr = cond.expressions[part.index];
                                if (expr.kind == DataKind.Attribute || expr.tag == ExprTag.EntityAttribute) {
                                    var attr = model.getAttributeById(expr.value);
                                    txt = "<span class=\"eqjs-query-text-attr\">" + model.getAttributeText(attr, "{entity} {attr}") + "</span>";
                                }
                                else {
                                    txt = "<span class=\"eqjs-query-text-expr\">" + quoteExprText(expr) + "</span>";
                                }
                            }
                            else if (part.type == "operator") {
                                txt = "<span class=\"eqjs-query-text-op\">" + txt + "</span>";
                            }
                            if (pi > 0)
                                condText += " ";
                            condText += txt;
                        }
                    }
                }
                if (condText) {
                    if (i > 0 && result) {
                        result += " " + conjText + " ";
                    }
                    result += condText;
                }
            }
            if (group.linkType == LinkType.None || group.linkType == LinkType.NotAll) {
                result = "<span class=\"eqjs-query-text-conj\">not</span> ( " + result + " )";
            }
            return result;
        };
        return "<div class=\"eqjs-query-text\">" + getConditionGroupText(this.getRootCondition()) + "</div>";
    };
    return Query;
}());
export { Query };
//# sourceMappingURL=query.js.map